import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ShoppingCart, ArrowLeft, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import CartDrawer from '@/components/common/CartDrawer';
import MenuItemCard from '@/components/menu/MenuItemCard';
import FixedFooter from '@/components/common/FixedFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlusOnePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCartCount } = useCart();
  
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(searchParams.get('search') ? true : false);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, selectedCategory, searchQuery]);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu/items?outlet=plus-one`);
      // Filter out unavailable items on frontend as well for extra safety
      const availableItems = response.data.filter(item => item.available !== false);
      setMenuItems(availableItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/menu/categories?outlet=plus-one`);
      const plusOneCategories = response.data['plus-one'] || [];
      setCategories(plusOneCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filterItems = () => {
    let filtered = [...menuItems];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const groupItemsByCategory = () => {
    const grouped = {};
    filteredItems.forEach(item => {
      const categoryKey = item.category;
      if (!grouped[categoryKey]) {
        grouped[categoryKey] = {};
      }
      
      // For wine and liquor categories, group by subcategory
      if ((item.category === 'wine' || item.category === 'liquor' || item.category === 'liquors') && item.subcategory) {
        const subKey = item.subcategory;
        if (!grouped[categoryKey][subKey]) {
          grouped[categoryKey][subKey] = [];
        }
        grouped[categoryKey][subKey].push(item);
      } else {
        // For other categories, use 'items' key
        if (!grouped[categoryKey].items) {
          grouped[categoryKey].items = [];
        }
        grouped[categoryKey].items.push(item);
      }
    });
    return grouped;
  };

  const formatCategoryName = (category) => {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--burgundy)' }}></div>
          <p style={{ color: 'var(--charcoal)' }}>Loading bar menu...</p>
        </div>
      </div>
    );
  }

  const groupedItems = groupItemsByCategory();

  return (
    <div className="min-h-screen pb-10">
      {/* Header - Same styling as Café Plus */}
      <header className="sticky top-0 z-40 glass-burgundy border-b" style={{ borderColor: 'rgba(224, 178, 59, 0.2)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-burgundy hover:opacity-80"
              style={{ color: 'var(--burgundy)' }}
              data-testid="back-to-home-button"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </button>

            <img
              src="/images/1 19.png"
              alt="Plus One"
              className="h-14"
            />

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-full glass hover:shadow-lg ${
                  showFilters ? 'bg-burgundy-light' : ''
                }`}
                style={{ color: 'var(--burgundy)' }}
                title="Toggle Filters"
              >
                <Filter size={20} />
              </button>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 rounded-full glass hover:shadow-lg"
                style={{ color: 'var(--burgundy)' }}
              >
                <Search size={20} />
              </button>
              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center gap-2 btn-gold"
                data-testid="cart-button"
              >
                <ShoppingCart size={20} />
                {getCartCount() > 0 && (
                  <span
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: 'var(--burgundy)' }}
                    data-testid="cart-count"
                  >
                    {getCartCount()}
                  </span>
                )}
                <span className="hidden sm:inline">Order</span>
              </button>
            </div>
          </div>

          {/* Search Bar - Toggleable */}
          {showSearch && (
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={20} style={{ color: 'var(--charcoal)' }} />
                <Input
                  type="text"
                  placeholder="Search cocktails, spirits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 rounded-full glass"
                  data-testid="search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="clear-search-button"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Filters - Toggleable */}
          {showFilters && (
            <div className="mt-4 flex items-center gap-3 overflow-x-auto pb-2" key="filters-bar">
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                <Filter size={16} />
                <span>Categories:</span>
              </div>

              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  selectedCategory === 'all' ? 'btn-primary' : 'glass'
                }`}
                data-testid="category-all-button"
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                    selectedCategory === category ? 'btn-primary' : 'glass'
                  }`}
                  data-testid={`category-${category}-button`}
                >
                  {formatCategoryName(category)}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Menu Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {Object.keys(groupedItems).length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl" style={{ color: 'var(--charcoal)' }}>No items found</p>
            <p className="text-sm mt-2" style={{ color: 'var(--charcoal)', opacity: 0.7 }}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          Object.entries(groupedItems).map(([category, categoryData]) => (
            <div key={category} className="mb-12">
              <h2 
                className="text-2xl sm:text-3xl mb-4"
                style={{ color: 'var(--burgundy)' }}
                data-testid={`category-header-${category}`}
              >
                {formatCategoryName(category)}
              </h2>
              
              {(category === 'wine' || category === 'liquor' || category === 'liquors') ? (
                // Wine and Liquor categories with subcategories
                Object.entries(categoryData).map(([subcategory, items]) => (
                  <div key={subcategory} className="mb-8">
                    <h3 
                      className="text-xl mb-4 font-medium"
                      style={{ color: 'var(--charcoal)' }}
                    >
                      {subcategory === 'red-wine-per-glass-bottle' ? 'RED WINE PER GLASS / BOTTLE' :
                       subcategory === 'white-wine-per-glass-bottle' ? 'WHITE WINE PER GLASS / BOTTLE' :
                       subcategory.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                      {items.map((item) => (
                        <MenuItemCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                // Other categories without subcategories
                <div className="grid grid-cols-1 gap-3">
                  {(categoryData.items || []).map((item) => (
                    <MenuItemCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </main>

      {/* Footer - Bar Information */}
      <footer className="mt-16 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="glass rounded-2xl p-6" style={{ backgroundColor: 'rgba(250, 246, 242, 0.8)' }}>
            <div className="space-y-2 text-sm" style={{ color: 'var(--charcoal)' }}>
              <p>• Drink Responsibly</p>
              <p>• IML/Beer will not be served to person below 21 years age</p>
              <p>• Standard Measure is 30ML</p>
              <p>• Prices quoted are exclusive discretionary service charge @5%, and government taxes will be levied as Applicable.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Fixed Footer */}
      <FixedFooter />
    </div>
  );
};

export default PlusOnePage;